import { useEffect, useState } from 'react'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'
import Typography from '@mui/material/Typography'
import InputField from '../../../../../FormControl/input/InputField'
import Dialog from '../../../../../dialog/Dialog'
import { Alert, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from '@mui/material'
import useAnalyticsCharts from '../../../../../../hooks/setting/useAnalyticsCharts'
import useFetchData from '../../../../../../hooks/useFetchData'
import TextEllipsis from '../../../../../text/TextEllipsis'
import CkeckboxRolesSkeleton from '../../../../../skeleton/CkeckboxRolesSkeleton'
import { useSelector } from 'react-redux'
import SelectField from '../../../../../FormControl/input/SelectField'

const BI_CUSTOM_CHARTS = [
  { value: 'AssessmentSummary', label: 'Assessment Summary' },
  { value: 'SurveyCompletion', label: 'Survey Completion' },
  { value: 'AssessmentByMonth', label: 'Assessment by Month' },
  { value: 'RoundProgress', label: 'Round Progress (Cohort)' },
  { value: 'PatAchievementBands', label: 'PAT — Achievement bands' },
  { value: 'PatScoreTrend', label: 'PAT — Scale score trend' },
  { value: 'PatMathsVsReading', label: 'PAT — Maths vs Reading' },
  { value: 'PatCoverage', label: 'PAT — Data coverage' },
  { value: 'PatStudentReport', label: 'PAT — Student report (item detail)' },
  { value: 'PatItemCohort', label: 'PAT — Cohort item performance' },
  { value: 'PeriodicalReportChart', label: 'Periodical Report (Student)' },
  { value: 'StudentSummaryReport', label: 'Student Summary (dashboard)' }
]
// react/prop-types disable-next-line
const AnalyticsChartDialog = ({ show, onClose, type, data = {} }) => {
  const {
    errors: errorsData,
    createAnalyticsChart,
    updateAnalyticsChart,
    deleteAnalyticsChart
  } = useAnalyticsCharts()
  const { isLoading: isLoadingRoles, data: dataRoles } = useFetchData('roles')
  const { isLoading: isLoadingMaxPosition, data: dataMaxPosition } = useFetchData(
    'setting/analytics-chart/max-position'
  )

  const curriculum = useSelector((state) => state?.assessment?.curriculum || [])

  const [form, setInputForm] = useState({})
  const [errors, setInputErrors] = useState(errorsData)
  const [loading, toggleLoading] = useState(false)

  const onChange = (e) => {
    const { name, value } = e.target
    if (name === 'accessRoleId') {
      const accessRoleId = form.accessRoleId.includes(value)
        ? form.accessRoleId.filter((role) => role !== value)
        : [...form.accessRoleId, value]
      const defaultForRoleId = (form.defaultForRoleId || []).filter((id) => accessRoleId.includes(id))
      setInputForm({ ...form, accessRoleId, defaultForRoleId })
      setInputErrors({ ...errors, accessRoleId: null })
    } else if (name === 'curriculumId') {
      setInputForm({ ...form, curriculumId: value, data: { ...form.data, curriculumId: value } })
      setInputErrors({ ...errors, curriculumId: null })
    } else if (name === 'defaultForRoleId') {
      const defaultForRoleId = (form.defaultForRoleId || []).includes(value)
        ? (form.defaultForRoleId || []).filter((id) => id !== value)
        : [...(form.defaultForRoleId || []), value]
      setInputForm({ ...form, defaultForRoleId })
    } else if (name === 'engine') {
      setInputForm({ ...form, engine: value, link: '' })
      setInputErrors({ ...errors, link: null })
    } else {
      setInputForm({ ...form, [name]: value })
      setInputErrors({ ...errors, [name]: null })
    }
  }

  const onSave = async (e) => {
    if (!['create', 'edit', 'delete'].includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      const formData = {
        ...form,
        accessRole: form.accessRoleId,
        defaultForRole: form.defaultForRoleId || [],
        data: form.data || {}
      }
      if (type === 'create') {
        result = await createAnalyticsChart(formData)
      }
      if (type === 'edit') {
        result = await updateAnalyticsChart(form._id, formData)
      }
      if (type === 'delete') {
        result = await deleteAnalyticsChart(form._id)
      }
      if (result.error) {
        setInputErrors(result)
      } else {
        onClose()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    setInputErrors(errorsData)
  }, [errorsData])

  useEffect(() => {
    setInputForm({
      ...data,
      engine: data.engine || 'tableau',
      accessRoleId: data.accessRole?.map((role) => role._id) || [],
      defaultForRoleId: data.defaultForRole || []
    })
  }, [data])

  useEffect(() => {
    if (isLoadingMaxPosition) return
    if (type === 'create' && dataMaxPosition) {
      setInputForm((old) => ({ ...old, position: dataMaxPosition }))
    }
  }, [isLoadingMaxPosition])

  const isPreview = type === 'preview'
  const isBiCustom = form.engine === 'bi-custom'

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={['fal', 'times']}
      >
        Cancel
      </ButtonStartIcon>
      {['create', 'edit', 'delete'].includes(type) && (
        <ButtonStartIcon
          onClick={onSave}
          color='primary'
          variant='contained'
          disabled={loading}
          loading={loading}
          startIcon={['fal', 'check']}
        >
          {type === 'delete' ? 'Confirm' : 'Save'}
        </ButtonStartIcon>
      )}
    </>
  )

  return (
    <Dialog
      id={`${type}-analytics-chart`}
      show={show}
      title={`${type} Analytics Chart`}
      onClose={onClose}
      actions={Actions}
      maxWidth={type === 'delete' ? 'xs' : 'sm'}
    >
      {type === 'delete' ? (
        <Typography component={'div'}>
          Are you sure you want to delete the year: <b>{form.name}</b>?
        </Typography>
      ) : (
        <div className='flex flex-col gap-4 py-4'>
          <div className='grid grid-cols-4 items-start gap-4'>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Position
            </Typography>
            <div className='col-span-3'>
              {isPreview ? (
                <Typography>{form.position}</Typography>
              ) : (
                <InputField
                  id='position'
                  name='position'
                  value={form.position}
                  error={errors?.position}
                  helperText={errors?.position}
                  onChange={onChange}
                  type='number'
                  fullWidth
                />
              )}
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Name
            </Typography>
            <div className='col-span-3'>
              {isPreview ? (
                <Typography>{form.name}</Typography>
              ) : (
                <InputField
                  id='name'
                  name='name'
                  value={form.name}
                  error={errors?.name}
                  helperText={errors?.name}
                  onChange={onChange}
                  fullWidth
                />
              )}
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Engine
            </Typography>
            <div className='col-span-3'>
              {isPreview ||
              (['analytics-curriculum', 'internal', 'tableau'].includes(form.engine) &&
                type === 'preview') ? (
                <Typography>{form.internal ? 'internal' : form.engine || 'tableau'}</Typography>
              ) : (
                <FormControl fullWidth size='small'>
                  <InputLabel id='engine-label'>Engine</InputLabel>
                  <Select
                    labelId='engine-label'
                    name='engine'
                    value={form.engine || 'tableau'}
                    label='Engine'
                    onChange={onChange}
                  >
                    <MenuItem value='tableau'>Tableau (External)</MenuItem>
                    <MenuItem value='bi-custom'>BI Custom (DuckDB)</MenuItem>
                    <MenuItem value='analytics-curriculum'>Analytics Curriculum</MenuItem>
                    <MenuItem value='internal'>Internal</MenuItem>
                  </Select>
                </FormControl>
              )}
            </div>
            {form.engine === 'analytics-curriculum' && (
              <>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Curriculum ID
                </Typography>
                <div className='col-span-3'>
                  {isPreview || ['edit'].includes(type) ? (
                    <Typography>
                      {curriculum?.find((item) => item._id === form?.data?.curriculumId)?.name}
                    </Typography>
                  ) : (
                    <SelectField
                      error={!!errors?.curriculumId}
                      helperText={errors?.curriculumId}
                      value={form?.data?.curriculumId}
                      name='curriculumId'
                      onChange={onChange}
                      options={curriculum?.map((item) => ({ value: item._id, label: item.name })) || []}
                      placeholder='Choose a curriculum'
                      onClear={() => onChange({ target: { name: 'curriculumId', value: '' } })}
                    />
                  )}
                </div>
              </>
            )}
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Link
            </Typography>
            <div className='col-span-3'>
              {isPreview || form.internal ? (
                <Typography>{form.link}</Typography>
              ) : isBiCustom ? (
                <FormControl fullWidth size='small'>
                  <InputLabel id='link-bi-label'>Chart</InputLabel>
                  <Select
                    labelId='link-bi-label'
                    name='link'
                    value={form.link || ''}
                    label='Chart'
                    onChange={onChange}
                    error={!!errors?.link}
                  >
                    {BI_CUSTOM_CHARTS.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <InputField
                  id='link'
                  name='link'
                  value={form.link}
                  error={errors?.link}
                  helperText={errors?.link}
                  onChange={onChange}
                  fullWidth
                />
              )}
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Access by roles
            </Typography>
            <div className='col-span-3 grid grid-cols-3 gap-4'>
              {isLoadingRoles ? (
                <CkeckboxRolesSkeleton />
              ) : (
                dataRoles?.map((role, index) => (
                  <div key={`access-by-roles-loading-${index}-${role._id}`} className='overflow-hidden'>
                    <FormControlLabel
                      control={
                        <Checkbox
                          readOnly={isPreview}
                          checked={form.accessRoleId.includes(role._id)}
                          name='accessRoleId'
                          value={role._id}
                          onChange={onChange}
                          color='primary'
                        />
                      }
                      label={
                        <TextEllipsis id={`text-access-by-roles-loading-${index}-${role._id}`}>
                          {role.name}
                        </TextEllipsis>
                      }
                    />
                  </div>
                ))
              )}
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Default for role
            </Typography>
            <div className='col-span-3 grid grid-cols-3 gap-4'>
              {isLoadingRoles ? (
                <CkeckboxRolesSkeleton />
              ) : (
                dataRoles
                  ?.filter((role) => form.accessRoleId?.includes(role._id))
                  .map((role, index) => (
                    <div key={`default-for-role-${index}-${role._id}`} className='overflow-hidden'>
                      <FormControlLabel
                        control={
                          <Checkbox
                            readOnly={isPreview}
                            checked={(form.defaultForRoleId || []).includes(role._id)}
                            name='defaultForRoleId'
                            value={role._id}
                            onChange={onChange}
                            color='primary'
                          />
                        }
                        label={
                          <TextEllipsis id={`text-default-for-role-${index}-${role._id}`}>
                            {role.name}
                          </TextEllipsis>
                        }
                      />
                    </div>
                  ))
              )}
              {!isLoadingRoles && !dataRoles?.some((role) => form.accessRoleId?.includes(role._id)) && (
                <Typography variant='body2' color='text.secondary' className='col-span-3'>
                  Select roles in &quot;Access by roles&quot; first.
                </Typography>
              )}
            </div>
          </div>
        </div>
      )}
      {errors && errors.message && (
        <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
          {errors.message}
        </Alert>
      )}
    </Dialog>
  )
}

export default AnalyticsChartDialog
